import React, { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader, Card, Button, Modal, Field, Input, Select, Empty, Badge } from "@/components/ui-kit";
import { FileImage, Plus, Trash2, Download, Wand2, Settings, ShieldCheck, Pencil, Search, Award, Gift, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/utils";

const KIND_META = {
  diplome: { label: "Diplôme", icon: Award, color: "green" },
  bon_cadeau: { label: "Bon cadeau", icon: Gift, color: "purple" },
  custom: { label: "Personnalisé", icon: FileText, color: "blue" },
};

const emptyTemplate = { name: "", format: "html", kind: "custom", orientation: "portrait", content: "" };

export default function Infographics() {
  const [tab, setTab] = useState("canevas");
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [members, setMembers] = useState([]);
  const [counter, setCounter] = useState({ year: new Date().getFullYear(), value: 0 });

  // template editor
  const [tmplModal, setTmplModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTemplate);
  const [mapping, setMapping] = useState({});

  // counter
  const [counterModal, setCounterModal] = useState(false);
  const [startInput, setStartInput] = useState("");

  // PDF canvas field editor
  const pdfRef = React.useRef(null);
  const [pdfModal, setPdfModal] = useState(null); // template being configured
  const [fields, setFields] = useState([]);
  const [selField, setSelField] = useState(0);
  const [dragIdx, setDragIdx] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const token = localStorage.getItem("fp_token");
  const previewUrl = (t) => `${process.env.REACT_APP_BACKEND_URL}/api/infographics/templates/${t.id}/preview.png?auth=${token}`;

  // generation
  const [genTemplate, setGenTemplate] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState([]);

  const loadTemplates = () => api.get("/infographics/templates").then((r) => setTemplates(r.data)).catch(() => {});
  const loadDocuments = () => api.get("/infographics/documents").then((r) => setDocuments(r.data)).catch(() => {});
  const loadCounter = () => api.get("/infographics/counter").then((r) => setCounter(r.data)).catch(() => {});

  useEffect(() => {
    loadTemplates();
    loadDocuments();
    loadCounter();
    api.get("/infographics/variables").then((r) => setVariables(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const openNewTemplate = () => { setEditing(null); setForm(emptyTemplate); setMapping({}); setTmplModal(true); };

  const importRef = React.useRef(null);
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["html", "htm", "svg"].includes(ext)) { toast.error("Formats acceptés : .html ou .svg"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 2 Mo)"); return; }
    try {
      const text = await file.text();
      const fmt2 = ext === "svg" ? "svg" : "html";
      let orientation = "portrait";
      if (fmt2 === "svg") {
        const tag = (text.match(/<svg\b[^>]*>/i) || [""])[0];
        const w = tag.match(/\bwidth\s*=\s*["']([0-9.]+)/i);
        const h = tag.match(/\bheight\s*=\s*["']([0-9.]+)/i);
        const vb = tag.match(/\bviewBox\s*=\s*["']([^"']+)["']/i);
        let wn, hn;
        if (w && h) { wn = parseFloat(w[1]); hn = parseFloat(h[1]); }
        else if (vb) { const p = vb[1].replace(/,/g, " ").split(/\s+/); wn = parseFloat(p[2]); hn = parseFloat(p[3]); }
        if (wn && hn && wn > hn) orientation = "landscape";
      } else if (/landscape/i.test(text)) {
        orientation = "landscape";
      }
      setEditing(null);
      setForm({
        name: file.name.replace(/\.(html?|svg)$/i, ""),
        format: fmt2, kind: "custom", orientation, content: text,
      });
      setMapping({});
      setTmplModal(true);
      toast.success("Canevas importé — son format est conservé, complétez le mapping puis enregistrez");
    } catch { toast.error("Lecture du fichier impossible"); }
  };
  const openEditTemplate = (t) => {
    setEditing(t);
    setForm({ name: t.name, format: t.format, kind: t.kind || "custom", orientation: t.page?.orientation || "portrait", content: t.content });
    setMapping(t.mapping || {});
    setTmplModal(true);
  };

  const detectedPlaceholders = (content) => {
    const out = []; const seen = new Set();
    const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g; let m;
    while ((m = re.exec(content || "")) !== null) { if (!seen.has(m[1])) { seen.add(m[1]); out.push(m[1]); } }
    return out;
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, format: form.format, kind: form.kind, content: form.content,
      page: { size: "A4", orientation: form.orientation }, mapping,
    };
    try {
      if (editing) {
        await api.put(`/infographics/templates/${editing.id}`, payload);
        await api.put(`/infographics/templates/${editing.id}/mapping`, { mapping });
      } else {
        await api.post("/infographics/templates", payload);
      }
      toast.success("Canevas enregistré");
      setTmplModal(false); loadTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Supprimer le canevas « ${t.name} » ?`)) return;
    try { await api.delete(`/infographics/templates/${t.id}`); toast.success("Supprimé"); loadTemplates(); }
    catch { toast.error("Erreur"); }
  };

  const onImportPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if ((file.name.split(".").pop() || "").toLowerCase() !== "pdf") { toast.error("Veuillez importer un fichier PDF"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF trop volumineux (max 15 Mo)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name.replace(/\.pdf$/i, ""));
    try {
      const { data } = await api.post("/infographics/templates/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF importé — placez les champs puis enregistrez");
      loadTemplates();
      openPdfEditor(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Import PDF échoué"); }
  };

  const openPdfEditor = (t) => {
    setPdfModal(t);
    setFields((t.fields || []).map((f) => ({ ...f })));
    setSelField(0);
  };
  const addField = (type) => {
    setFields((fs) => {
      const nf = type === "image"
        ? { key: "radar_chart", type: "image", page: 0, x: 40, y: 40, w: 25, h: 20, size: 14, color: "#111111", align: "left" }
        : { key: "full_name", type: "text", page: 0, x: 50, y: 50, size: 18, color: "#14532d", align: "center", w: 20, h: 20 };
      const next = [...fs, nf];
      setSelField(next.length - 1);
      return next;
    });
  };
  const updateField = (i, patch) => setFields((fs) => fs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeField = (i) => setFields((fs) => fs.filter((_, idx) => idx !== i));
  const onPreviewClick = (e) => {
    if (dragIdx !== null) return;
    if (!fields.length) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 1000) / 10;
    const y = Math.round(((e.clientY - r.top) / r.height) * 1000) / 10;
    updateField(selField, { x, y });
  };
  const onDragMove = (e) => {
    if (dragIdx === null) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, Math.round(((e.clientX - r.left) / r.width) * 1000) / 10));
    const y = Math.max(0, Math.min(100, Math.round(((e.clientY - r.top) / r.height) * 1000) / 10));
    updateField(dragIdx, { x, y });
  };
  const savePdfFields = async () => {
    try {
      await api.put(`/infographics/templates/${pdfModal.id}/fields`, { fields });
      toast.success("Champs enregistrés");
      setPdfModal(null); loadTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const runPreview = async (payload) => {
    setPreviewLoading(true);
    try {
      const res = await api.post("/infographics/preview", payload, { responseType: "blob" });
      setPreviewSrc(URL.createObjectURL(res.data));
    } catch {
      toast.error("Aperçu impossible — vérifiez le canevas et les données");
    } finally { setPreviewLoading(false); }
  };
  const closePreview = () => { if (previewSrc) URL.revokeObjectURL(previewSrc); setPreviewSrc(null); };

  const saveCounter = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/infographics/counter", { year: counter.year, start: parseInt(startInput, 10) || 0 });
      toast.success(`Prochain numéro : ${data.next}`);
      setCounterModal(false); loadCounter();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const toggleMember = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const generate = async () => {
    if (!genTemplate) { toast.error("Choisissez un canevas"); return; }
    if (!selected.size) { toast.error("Sélectionnez au moins un affilié"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/infographics/generate", {
        template_id: genTemplate, member_ids: Array.from(selected), custom_message: message,
      });
      setLastGenerated(data.documents || []);
      toast.success(`${data.generated} document(s) généré(s)`);
      loadDocuments(); loadCounter();
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de la génération"); }
    finally { setGenerating(false); }
  };

  const download = (doc) => downloadFile(`/infographics/documents/${doc.id}/download`, `${doc.doc_number}.pdf`)
    .catch(() => toast.error("Téléchargement échoué"));

  const verify = async (doc) => {
    try {
      const { data } = await api.get(`/infographics/documents/${doc.id}/verify`);
      data.valid ? toast.success(`Intégrité OK · ${doc.doc_number}`) : toast.error("Hash NON conforme — document altéré !");
    } catch { toast.error("Vérification impossible"); }
  };

  const shownMembers = members.filter((m) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${m.first_name} ${m.last_name} ${m.category || ""}`.toLowerCase().includes(q);
  });
  const phList = detectedPlaceholders(form.content);

  return (
    <div>
      <PageHeader title="Infographies & PDF" subtitle="Canevas dynamiques, injection des données affiliés et génération de PDF numérotés."
        action={(
          <div className="flex gap-2 flex-wrap">
            <input ref={importRef} type="file" accept=".html,.htm,.svg" onChange={onImportFile} className="hidden" data-testid="import-canvas-input" />
            <input ref={pdfRef} type="file" accept=".pdf,application/pdf" onChange={onImportPdf} className="hidden" data-testid="import-pdf-input" />
            <Button data-testid="counter-settings-btn" variant="outline" onClick={() => { setStartInput(String((counter.value || 0) + 1)); setCounterModal(true); }}>
              <Settings size={16} /> Numéro de départ
            </Button>
            <Button data-testid="import-pdf-btn" variant="outline" onClick={() => pdfRef.current?.click()}>
              <FileText size={16} /> Importer un PDF
            </Button>
            <Button data-testid="import-canvas-btn" variant="outline" onClick={() => importRef.current?.click()}>
              <Upload size={16} /> Importer un canevas
            </Button>
            <Button data-testid="new-template-btn" onClick={openNewTemplate}><Plus size={16} /> Nouveau canevas</Button>
          </div>
        )} />

      <div className="flex gap-2 mb-6 border-b border-border">
        {[["canevas", "Canevas"], ["generation", "Génération"], ["documents", "Documents"]].map(([k, lbl]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-bold uppercase tracking-wide -mb-px border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {lbl}
          </button>
        ))}
      </div>

      {/* ---------------- CANEVAS ---------------- */}
      {tab === "canevas" && (templates.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => {
            const K = KIND_META[t.kind] || KIND_META.custom;
            return (
              <Card key={t.id} data-testid={`template-card-${t.id}`} className="flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-lg bg-primary/15 flex items-center justify-center text-primary"><K.icon size={20} /></div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-template-${t.id}`} onClick={() => (t.format === "pdf" ? openPdfEditor(t) : openEditTemplate(t))} className="p-2 text-muted-foreground hover:text-primary"><Pencil size={15} /></button>
                    <button data-testid={`delete-template-${t.id}`} onClick={() => deleteTemplate(t)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                  </div>
                </div>
                <p className="font-bold text-lg mt-3">{t.name}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge color={K.color}>{K.label}</Badge>
                  <Badge color={t.format === "pdf" ? "purple" : "gray"}>{t.format.toUpperCase()}</Badge>
                  <Badge color="gray">{t.page?.orientation === "landscape" ? "Paysage" : "Portrait"}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-3">{(t.placeholders || []).length} champ(s) : {(t.placeholders || []).map((p) => p.key).join(", ") || "—"}</p>
                {t.format === "pdf" && (
                  <Button data-testid={`config-fields-${t.id}`} variant="outline" className="mt-3" onClick={() => openPdfEditor(t)}>
                    <Pencil size={15} /> Placer les champs
                  </Button>
                )}
                <Button data-testid={`use-template-${t.id}`} variant="outline" className="mt-2" onClick={() => { setGenTemplate(t.id); setTab("generation"); }}>
                  <Wand2 size={15} /> Générer avec ce canevas
                </Button>
              </Card>
            );
          })}
        </div>
      ) : <Empty text="Aucun canevas. Créez-en un ou utilisez les modèles par défaut (Diplôme, Bon cadeau)." />)}

      {/* ---------------- GENERATION ---------------- */}
      {tab === "generation" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="font-bold mb-4 flex items-center gap-2"><Wand2 size={18} className="text-primary" /> Paramètres</h3>
            <Field label="Canevas">
              <Select data-testid="gen-template-select" value={genTemplate} onChange={(e) => setGenTemplate(e.target.value)}>
                <option value="">— Choisir un canevas —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </Field>
            <Field label="Message personnalisé (optionnel)">
              <textarea data-testid="gen-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                placeholder="Ex. Félicitations pour ta superbe saison !"
                className="w-full rounded-lg bg-secondary/40 border border-border p-3 text-sm resize-y focus:outline-none focus:border-primary" />
            </Field>
            <div className="flex gap-2 mt-2">
              <Button data-testid="preview-gen-btn" variant="outline" className="flex-1" disabled={previewLoading || !genTemplate}
                onClick={() => runPreview({ template_id: genTemplate, member_id: Array.from(selected)[0], custom_message: message })}>
                <FileImage size={16} /> {previewLoading ? "Rendu..." : "Aperçu"}
              </Button>
              <Button data-testid="generate-btn" className="flex-1" onClick={generate} disabled={generating}>
                <FileImage size={16} /> {generating ? "Génération..." : `Générer (${selected.size})`}
              </Button>
            </div>
            {lastGenerated.length > 0 && (
              <div className="mt-5" data-testid="generated-list">
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Derniers documents générés</p>
                <div className="space-y-2">
                  {lastGenerated.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-secondary/40 border border-border">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{d.member_name}</p>
                        <p className="text-[11px] font-mono text-muted-foreground">{d.doc_number}</p>
                      </div>
                      <button data-testid={`dl-generated-${d.id}`} onClick={() => download(d)} className="h-8 px-3 rounded-lg bg-primary/15 text-primary text-xs font-bold flex items-center gap-1 hover:bg-primary/25 transition-colors"><Download size={14} /> PDF</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Card>
            <h3 className="font-bold mb-3">Sélection des affiliés</h3>
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="member-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un affilié..."
                className="w-full pl-9 pr-3 h-11 rounded-lg bg-secondary/40 border border-border text-sm focus:outline-none focus:border-primary" />
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground" data-testid="selected-count">{selected.size} sélectionné(s)</span>
              {selected.size > 0 && <button data-testid="clear-selection" onClick={() => setSelected(new Set())} className="text-xs font-bold text-primary hover:underline">Tout désélectionner</button>}
            </div>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {shownMembers.map((m) => {
                const checked = selected.has(m.id);
                return (
                  <button key={m.id} data-testid={`member-${m.id}`} onClick={() => toggleMember(m.id)}
                    className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg border text-left transition-colors ${checked ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:border-primary/50"}`}>
                    <span className="text-sm font-semibold truncate">{m.first_name} {m.last_name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {m.category && <Badge color="gray">{m.category}</Badge>}
                      <span className={`w-5 h-5 rounded flex items-center justify-center border ${checked ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>{checked && "✓"}</span>
                    </div>
                  </button>
                );
              })}
              {!shownMembers.length && <Empty text="Aucun affilié." />}
            </div>
          </Card>
        </div>
      )}

      {/* ---------------- DOCUMENTS ---------------- */}
      {tab === "documents" && (documents.length ? (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-secondary/60 text-muted-foreground text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Numéro</th>
                <th className="text-left px-4 py-3">Affilié</th>
                <th className="text-left px-4 py-3">Canevas</th>
                <th className="text-left px-4 py-3">Généré le</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-xs">{d.doc_number}</td>
                  <td className="px-4 py-3 font-semibold">{d.member_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{d.template_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDateTime(d.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`verify-${d.id}`} onClick={() => verify(d)} className="h-8 px-3 rounded-lg bg-secondary text-muted-foreground hover:text-primary text-xs font-bold flex items-center gap-1 transition-colors"><ShieldCheck size={14} /> Vérifier</button>
                      <button data-testid={`dl-${d.id}`} onClick={() => download(d)} className="h-8 px-3 rounded-lg bg-primary/15 text-primary text-xs font-bold flex items-center gap-1 hover:bg-primary/25 transition-colors"><Download size={14} /> PDF</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="md:hidden divide-y divide-border">
            {documents.map((d) => (
              <div key={d.id} data-testid={`doc-card-${d.id}`} className="p-3">
                <p className="font-semibold">{d.member_name}</p>
                <p className="text-[11px] font-mono text-muted-foreground">{d.doc_number}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{d.template_name} · {fmtDateTime(d.created_at)}</p>
                <div className="flex gap-2 mt-2">
                  <button data-testid={`verify-m-${d.id}`} onClick={() => verify(d)} className="flex-1 h-9 rounded-lg bg-secondary text-muted-foreground text-xs font-bold flex items-center justify-center gap-1"><ShieldCheck size={14} /> Vérifier</button>
                  <button data-testid={`dl-m-${d.id}`} onClick={() => download(d)} className="flex-1 h-9 rounded-lg bg-primary/15 text-primary text-xs font-bold flex items-center justify-center gap-1"><Download size={14} /> PDF</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : <Empty text="Aucun document généré pour l'instant." />)}

      {/* ---------------- Template editor modal ---------------- */}
      <Modal open={tmplModal} onClose={() => setTmplModal(false)} title={editing ? "Modifier le canevas" : "Nouveau canevas"}>
        <form onSubmit={saveTemplate}>
          <Field label="Nom"><Input data-testid="template-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. Diplôme fin de saison" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Format">
              <Select data-testid="template-format" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
                <option value="html">HTML/CSS</option><option value="svg">SVG</option>
              </Select>
            </Field>
            <Field label="Type">
              <Select data-testid="template-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="custom">Personnalisé</option><option value="diplome">Diplôme</option><option value="bon_cadeau">Bon cadeau</option>
              </Select>
            </Field>
            <Field label="Orientation">
              <Select data-testid="template-orientation" value={form.orientation} onChange={(e) => setForm({ ...form, orientation: e.target.value })}>
                <option value="portrait">Portrait</option><option value="landscape">Paysage</option>
              </Select>
            </Field>
          </div>
          <Field label="Contenu du canevas (HTML/SVG avec variables {{ nom }})">
            <textarea data-testid="template-content" required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10}
              className="w-full rounded-lg bg-secondary/40 border border-border p-3 text-xs font-mono resize-y focus:outline-none focus:border-primary" />
          </Field>
          <div className="rounded-lg bg-secondary/40 border border-border p-3 mb-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Variables disponibles (cliquez pour copier la balise)</p>
            <div className="flex flex-wrap gap-1.5">
              {variables.map((v) => (
                <button key={v.key} type="button" data-testid={`var-chip-${v.key}`}
                  onClick={() => { navigator.clipboard?.writeText(`{{${v.key}}}`); toast.success(`{{${v.key}}} copié`); }}
                  className="px-2 py-0.5 rounded-full bg-secondary border border-border text-[11px] font-mono hover:border-primary hover:text-primary transition-colors" title={v.label}>
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
          </div>
          {phList.length > 0 && (
            <div className="mb-4" data-testid="mapping-zone">
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Mapping des champs détectés</p>
              <div className="space-y-2">
                {phList.map((ph) => (
                  <div key={ph} className="flex items-center gap-2">
                    <span className="text-xs font-mono w-40 truncate">{`{{${ph}}}`}</span>
                    <span className="text-muted-foreground">→</span>
                    <div className="flex-1">
                      <Select data-testid={`map-${ph}`} value={mapping[ph] || ph} onChange={(e) => setMapping((mp) => ({ ...mp, [ph]: e.target.value }))}>
                        {variables.map((v) => <option key={v.key} value={v.key}>{v.label} ({v.key})</option>)}
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button data-testid="save-template-btn" className="w-full">{editing ? "Mettre à jour" : "Créer le canevas"}</Button>
        </form>
      </Modal>

      {/* ---------------- PDF field editor modal ---------------- */}
      <Modal open={!!pdfModal} onClose={() => setPdfModal(null)} title={`Placer les champs — ${pdfModal?.name || ""}`}>
        {pdfModal && (
          <div data-testid="pdf-field-editor">
            <p className="text-sm text-muted-foreground mb-3">
              Glissez un champ sur l'aperçu pour le déplacer (ou cliquez sur une zone vide pour y placer le champ sélectionné). Les variables seront injectées à la génération.
            </p>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="relative border border-border rounded-lg overflow-hidden bg-white" data-testid="pdf-preview-wrap"
                onClick={onPreviewClick} onMouseMove={onDragMove} onMouseUp={() => setDragIdx(null)} onMouseLeave={() => setDragIdx(null)}
                style={{ cursor: dragIdx !== null ? "grabbing" : "crosshair" }}>
                <img src={previewUrl(pdfModal)} alt="aperçu" className="w-full block select-none" draggable={false} />
                {fields.map((f, i) => (
                  <div key={i} data-testid={`field-marker-${i}`}
                    onMouseDown={(e) => { e.stopPropagation(); setSelField(i); setDragIdx(i); }}
                    onClick={(e) => e.stopPropagation()}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap cursor-move select-none ${i === selField ? "bg-primary text-primary-foreground ring-2 ring-primary" : "bg-black/70 text-white"}`}
                    style={{ left: `${f.x}%`, top: `${f.y}%` }}>
                    {f.type === "image" ? "🖼 " : ""}{f.key}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex gap-2 mb-3">
                  <Button type="button" data-testid="add-text-field" variant="outline" onClick={() => addField("text")}><Plus size={14} /> Texte</Button>
                  <Button type="button" data-testid="add-image-field" variant="outline" onClick={() => addField("image")}><Plus size={14} /> Image</Button>
                </div>
                <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-1">
                  {fields.length ? fields.map((f, i) => (
                    <div key={i} data-testid={`field-row-${i}`} onClick={() => setSelField(i)}
                      className={`p-2.5 rounded-lg border cursor-pointer ${i === selField ? "border-primary bg-primary/10" : "border-border bg-secondary/40"}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge color={f.type === "image" ? "purple" : "blue"}>{f.type === "image" ? "Image" : "Texte"}</Badge>
                        <div className="flex-1">
                          <Select data-testid={`field-var-${i}`} value={f.key} onChange={(e) => updateField(i, { key: e.target.value })}>
                            {variables.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                          </Select>
                        </div>
                        <button type="button" data-testid={`field-del-${i}`} onClick={(e) => { e.stopPropagation(); removeField(i); }} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <label className="flex flex-col gap-0.5">X %<Input data-testid={`field-x-${i}`} type="number" value={f.x} onChange={(e) => updateField(i, { x: parseFloat(e.target.value) })} /></label>
                        <label className="flex flex-col gap-0.5">Y %<Input type="number" value={f.y} onChange={(e) => updateField(i, { y: parseFloat(e.target.value) })} /></label>
                        {f.type === "image" ? (<>
                          <label className="flex flex-col gap-0.5">L %<Input type="number" value={f.w} onChange={(e) => updateField(i, { w: parseFloat(e.target.value) })} /></label>
                          <label className="flex flex-col gap-0.5">H %<Input type="number" value={f.h} onChange={(e) => updateField(i, { h: parseFloat(e.target.value) })} /></label>
                        </>) : (<>
                          <label className="flex flex-col gap-0.5">Taille<Input type="number" value={f.size} onChange={(e) => updateField(i, { size: parseFloat(e.target.value) })} /></label>
                          <label className="flex flex-col gap-0.5">Couleur<input type="color" value={f.color} onChange={(e) => updateField(i, { color: e.target.value })} className="h-9 w-full rounded-lg bg-secondary/40 border border-border" /></label>
                        </>)}
                      </div>
                      {f.type !== "image" && (
                        <div className="flex gap-1.5 mt-2">
                          {[["left", "Gauche"], ["center", "Centré"], ["right", "Droite"]].map(([a, lbl]) => (
                            <button key={a} type="button" onClick={(e) => { e.stopPropagation(); updateField(i, { align: a }); }}
                              className={`px-2 py-1 rounded text-[11px] font-bold ${f.align === a ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{lbl}</button>
                          ))}
                        </div>
                      )}
                      {(pdfModal.page_count || 1) > 1 && (
                        <label className="flex items-center gap-2 text-xs mt-2">Page
                          <Input type="number" min="1" value={(f.page || 0) + 1} onChange={(e) => updateField(i, { page: Math.max(0, (parseInt(e.target.value, 10) || 1) - 1) })} className="w-20" /></label>
                      )}
                    </div>
                  )) : <Empty text="Ajoutez un champ texte ou image." />}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button data-testid="preview-fields-btn" variant="outline" className="flex-1" disabled={previewLoading}
                onClick={() => runPreview({ template_id: pdfModal.id, fields, member_id: Array.from(selected)[0] })}>
                <FileImage size={16} /> {previewLoading ? "Rendu..." : "Aperçu témoin"}
              </Button>
              <Button data-testid="save-fields-btn" className="flex-1" onClick={savePdfFields}>Enregistrer les champs</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ---------------- Preview modal ---------------- */}
      <Modal open={!!previewSrc} onClose={closePreview} title="Aperçu (affilié témoin)">
        <p className="text-sm text-muted-foreground mb-3">Rendu avec un affilié témoin — aucune génération ni numérotation définitive.</p>
        <iframe title="apercu" data-testid="preview-frame" src={previewSrc || ""} className="w-full h-[68vh] rounded-lg border border-border bg-white" />
      </Modal>

      {/* ---------------- Counter modal ---------------- */}
      <Modal open={counterModal} onClose={() => setCounterModal(false)} title="Numérotation des documents">
        <form onSubmit={saveCounter}>
          <p className="text-sm text-muted-foreground mb-4">
            Format : <span className="font-mono">REF-{counter.year}-&lt;ID&gt;-&lt;N°&gt;</span>. Définissez le prochain numéro de séquence pour l'année {counter.year}.
          </p>
          <Field label={`Prochain numéro (année ${counter.year})`}>
            <Input data-testid="counter-start" type="number" min="1" required value={startInput} onChange={(e) => setStartInput(e.target.value)} />
          </Field>
          <Button data-testid="save-counter-btn" className="w-full mt-2">Enregistrer</Button>
        </form>
      </Modal>
    </div>
  );
}
